import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';

const SpecialEvaluationConfig = () => {
  const { projectId, subjectId, evaluationType } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useApp();

  const [selectedIndicator, setSelectedIndicator] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [indicatorDescription, setIndicatorDescription] = useState('');
  const [addedIndicators, setAddedIndicators] = useState([]);
  const [loading, setLoading] = useState(true);

  // Indicadores disponibles según el tipo de evaluación
  const [availableIndicators, setAvailableIndicators] = useState([]);

  useEffect(() => {
    loadEvaluationData();
  }, [projectId, subjectId, evaluationType]);

  const loadEvaluationData = async () => {
    try {
      setLoading(true);

      // Mock data - aquí se haría la llamada real a la API para obtener los indicadores
      // configurados en la sección de administración
      const mockIndicators = [
        {
          id: 'ind1',
          title: 'Participación activa',
          description: 'Evalúa el nivel de participación del estudiante en las actividades del proyecto',
          type: evaluationType === 'self-evaluation' ? 'autoevaluacion' : 'coevaluacion'
        },
        {
          id: 'ind2',
          title: 'Trabajo colaborativo',
          description: 'Evalúa la capacidad de trabajar en equipo y colaborar con otros estudiantes',
          type: evaluationType === 'self-evaluation' ? 'autoevaluacion' : 'coevaluacion'
        },
        {
          id: 'ind3',
          title: 'Responsabilidad',
          description: 'Evalúa el cumplimiento de plazos y compromisos adquiridos',
          type: evaluationType === 'self-evaluation' ? 'autoevaluacion' : 'coevaluacion'
        },
        {
          id: 'ind4',
          title: 'Comunicación efectiva',
          description: 'Evalúa la capacidad de expresar ideas de forma clara y escuchar activamente',
          type: evaluationType === 'self-evaluation' ? 'autoevaluacion' : 'coevaluacion'
        },
      ];

      setAvailableIndicators(mockIndicators);

      // Cargar indicadores ya agregados
      setAddedIndicators([]);
    } catch (error) {
      showError('Error al cargar los datos de evaluación');
    } finally {
      setLoading(false);
    }
  };

  const getEvaluationTitle = () => {
    return evaluationType === 'self-evaluation' ? 'Autoevaluación' : 'Coevaluación';
  };

  const getEvaluationIcon = () => {
    if (evaluationType === 'self-evaluation') {
      return (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    }
    return (
      <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    );
  };

  const handleIndicatorChange = (indicatorId) => {
    setSelectedIndicator(indicatorId);
    const indicator = availableIndicators.find(i => i.id === indicatorId);
    if (indicator) {
      setIndicatorDescription(indicator.description);
    }
  };

  const handleAddIndicator = () => {
    if (!selectedIndicator || !selectedType) {
      showError('Debes seleccionar un indicador y el tipo');
      return;
    }

    if (!indicatorDescription.trim()) {
      showError('Debes completar la descripción del indicador');
      return;
    }

    const indicator = availableIndicators.find(i => i.id === selectedIndicator);

    const newIndicator = {
      id: Date.now(),
      indicatorId: selectedIndicator,
      indicatorTitle: indicator.title,
      type: selectedType,
      description: indicatorDescription,
      status: 'pendiente',
      createdAt: new Date().toISOString()
    };

    setAddedIndicators([...addedIndicators, newIndicator]);

    // Resetear formulario
    setSelectedIndicator('');
    setSelectedType('');
    setIndicatorDescription('');

    showSuccess('Indicador agregado correctamente');
  };

  const handleDeleteIndicator = (indicatorId) => {
    setAddedIndicators(addedIndicators.filter(i => i.id !== indicatorId));
    showSuccess('Indicador eliminado');
  };

  const handleSaveConfiguration = () => {
    if (addedIndicators.length === 0) {
      showError('Debes agregar al menos un indicador');
      return;
    }

    // Aquí se guardaría en la API
    showSuccess('Configuración guardada correctamente');
    navigate(`/projects/${projectId}?tab=evaluations`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const themeColor = evaluationType === 'self-evaluation' ? 'blue' : 'purple';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/projects/${projectId}?tab=evaluations`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver a evaluaciones
          </button>

          <div className={`bg-gradient-to-br from-${themeColor}-50 to-${themeColor}-100 rounded-xl shadow-sm border border-${themeColor}-200 p-6`}>
            <div className="flex items-center space-x-4">
              {getEvaluationIcon()}
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Configuración de {getEvaluationTitle()}
                </h1>
                <p className="text-gray-600">
                  Configura los indicadores de evaluación que se utilizarán en esta evaluación
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Formulario para agregar indicador */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Agregar Indicador de Evaluación
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Selector de Indicador */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Indicador de Evaluación *
              </label>
              <select
                value={selectedIndicator}
                onChange={(e) => handleIndicatorChange(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">Seleccionar indicador...</option>
                {availableIndicators.map((indicator) => (
                  <option key={indicator.id} value={indicator.id}>
                    {indicator.title}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-2">
                Los indicadores disponibles se configuran en la sección de Administración
              </p>
            </div>

            {/* Selector de Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Indicador *
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                disabled={!selectedIndicator}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <option value="">Seleccionar tipo...</option>
                <option value="proceso">Proceso</option>
                <option value="producto">Producto</option>
              </select>
            </div>
          </div>

          {/* Descripción del indicador */}
          {selectedIndicator && selectedType && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción del Indicador *
              </label>
              <textarea
                value={indicatorDescription}
                onChange={(e) => setIndicatorDescription(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Edita o completa la descripción del indicador..."
              />
              <p className="text-xs text-gray-500 mt-2">
                Puedes modificar la descripción base del indicador para ajustarla según las necesidades del proyecto
              </p>
            </div>
          )}

          {/* Botón agregar */}
          <div className="flex justify-end">
            <button
              onClick={handleAddIndicator}
              disabled={!selectedIndicator || !selectedType || !indicatorDescription.trim()}
              className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Agregar Indicador
            </button>
          </div>
        </div>

        {/* Lista de indicadores agregados */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Indicadores Agregados ({addedIndicators.length})
            </h2>
          </div>

          {addedIndicators.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
              <p className="text-gray-500">No hay indicadores agregados</p>
              <p className="text-sm text-gray-400 mt-1">Agrega el primer indicador usando el formulario de arriba</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Indicadores de Proceso */}
              {addedIndicators.filter(i => i.type === 'proceso').length > 0 && (
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="bg-orange-100 text-orange-700 px-3 py-1 rounded-lg font-semibold text-sm">
                      Proceso
                    </div>
                    <span className="text-sm text-gray-500">
                      ({addedIndicators.filter(i => i.type === 'proceso').length} {addedIndicators.filter(i => i.type === 'proceso').length === 1 ? 'indicador' : 'indicadores'})
                    </span>
                  </div>
                  <div className="space-y-4">
                    {addedIndicators.filter(i => i.type === 'proceso').map((indicator, index) => (
                      <div key={indicator.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className="bg-primary-100 text-primary-700 rounded-lg px-3 py-1 text-sm font-semibold flex-shrink-0">
                              #{index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                  {indicator.indicatorTitle}
                                </span>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                  {indicator.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">{indicator.description}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteIndicator(indicator.id)}
                            className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                            title="Eliminar indicador"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Indicadores de Producto */}
              {addedIndicators.filter(i => i.type === 'producto').length > 0 && (
                <div>
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="bg-green-100 text-green-700 px-3 py-1 rounded-lg font-semibold text-sm">
                      Producto
                    </div>
                    <span className="text-sm text-gray-500">
                      ({addedIndicators.filter(i => i.type === 'producto').length} {addedIndicators.filter(i => i.type === 'producto').length === 1 ? 'indicador' : 'indicadores'})
                    </span>
                  </div>
                  <div className="space-y-4">
                    {addedIndicators.filter(i => i.type === 'producto').map((indicator, index) => (
                      <div key={indicator.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex items-start space-x-3 flex-1">
                            <div className="bg-primary-100 text-primary-700 rounded-lg px-3 py-1 text-sm font-semibold flex-shrink-0">
                              #{index + 1}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center space-x-2 mb-2">
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                                  {indicator.indicatorTitle}
                                </span>
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                  {indicator.status}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700">{indicator.description}</p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleDeleteIndicator(indicator.id)}
                            className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
                            title="Eliminar indicador"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="mt-6 flex justify-end space-x-4">
          <button
            onClick={() => navigate(`/projects/${projectId}?tab=evaluations`)}
            className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSaveConfiguration}
            disabled={addedIndicators.length === 0}
            className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            Guardar Configuración
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpecialEvaluationConfig;
